'use client'

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, TrendingDown, Search, X, Copy, Check } from 'lucide-react'

import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { FunctionsService } from '@/services/functions.service'
import { cn } from '@/lib/utils'

type Product = {
  id: string
  title: string
  inventory: number
  createdAt?: string | null
  handle?: string | null
  imageUrl?: string | null
  status?: string
  vendor?: string
  product_type?: string
  tags?: string
}

type SortOption = 'daysInInventory' | 'inventoryDesc' | 'inventoryAsc'

const sortLabels: Record<SortOption, string> = {
  daysInInventory: 'Más días en tienda',
  inventoryDesc: 'Mayor inventario',
  inventoryAsc: 'Menor inventario',
}

const calculateDays = (createdAt?: string | null): number | undefined => {
  if (!createdAt) return undefined
  const created = new Date(createdAt)
  if (isNaN(created.getTime())) return undefined
  return Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24))
}

export default function MenorRotacionPage() {
  const navigate = useNavigate()
  const [allProducts, setAllProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortOption>('daysInInventory')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all')
  const [inventoryRange, setInventoryRange] = useState<'all' | 'low' | 'medium' | 'high'>('all')
  const [brandFilter, setBrandFilter] = useState<string>('all')   // vendor
  const [lineFilter, setLineFilter] = useState<string>('all')     // product_type
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    FunctionsService.getProductsFromFirestore().then((response) => {
      if (response.data) {
        setAllProducts(response.data.products)
      }
      setLoading(false)
    })
  }, [])

  // Reset line filter when brand changes
  const handleBrandChange = useCallback((brand: string) => {
    setBrandFilter(brand)
    setLineFilter('all')
  }, [])

  const handleCopy = useCallback((product: Product) => {
    navigator.clipboard.writeText(product.title).then(() => {
      setCopiedId(product.id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }, [])

  // Pool base: products with inventory > 0 and > 30 days in store
  const basePool = useMemo(
    () =>
      allProducts
        .filter((p) => Number(p.inventory ?? 0) > 0)
        .filter((p) => (calculateDays(p.createdAt) ?? 0) > 30),
    [allProducts]
  )

  // Unique vendors (brands) — from the actual vendor field
  const brands = useMemo(() => {
    const set = new Set<string>()
    basePool.forEach((p) => {
      const v = p.vendor?.trim()
      if (v) set.add(v)
    })
    return Array.from(set).sort()
  }, [basePool])

  // Unique product types (lines) — filtered by selected brand
  const productTypes = useMemo(() => {
    const pool = brandFilter === 'all' ? basePool : basePool.filter((p) => p.vendor?.trim() === brandFilter)
    const set = new Set<string>()
    pool.forEach((p) => {
      const t = p.product_type?.trim()
      if (t) set.add(t)
    })
    return Array.from(set).sort()
  }, [basePool, brandFilter])

  const filtered = useMemo(() => {
    let result = [...basePool]

    if (statusFilter === 'active') result = result.filter((p) => p.status === 'active')
    else if (statusFilter === 'archived') result = result.filter((p) => p.status === 'archived')

    if (inventoryRange === 'low') result = result.filter((p) => Number(p.inventory) >= 1 && Number(p.inventory) <= 10)
    else if (inventoryRange === 'medium') result = result.filter((p) => Number(p.inventory) > 10 && Number(p.inventory) <= 50)
    else if (inventoryRange === 'high') result = result.filter((p) => Number(p.inventory) > 50)

    if (brandFilter !== 'all') result = result.filter((p) => p.vendor?.trim() === brandFilter)

    if (lineFilter !== 'all') result = result.filter((p) => p.product_type?.trim() === lineFilter)

    if (search) result = result.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))

    switch (sort) {
      case 'daysInInventory':
        result.sort((a, b) => (calculateDays(b.createdAt) ?? 0) - (calculateDays(a.createdAt) ?? 0))
        break
      case 'inventoryDesc':
        result.sort((a, b) => Number(b.inventory) - Number(a.inventory))
        break
      case 'inventoryAsc':
        result.sort((a, b) => Number(a.inventory) - Number(b.inventory))
        break
    }

    return result
  }, [basePool, search, sort, statusFilter, inventoryRange, brandFilter, lineFilter])

  const totalInFiltered = useMemo(
    () => filtered.reduce((sum, p) => sum + Number(p.inventory ?? 0), 0),
    [filtered]
  )

  const hasActiveFilters =
    statusFilter !== 'all' ||
    inventoryRange !== 'all' ||
    brandFilter !== 'all' ||
    lineFilter !== 'all' ||
    search !== ''

  const clearFilters = () => {
    setStatusFilter('all')
    setInventoryRange('all')
    setBrandFilter('all')
    setLineFilter('all')
    setSearch('')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 space-y-4">
        <Header />

        {/* Back + Title */}
        <div className="flex items-start gap-3">
          <Button
            variant="outline"
            size="sm"
            className="flex-shrink-0 border-white/10 bg-white/10 text-white hover:bg-white/20"
            onClick={() => navigate('/productos')}
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Volver
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-white">
              <TrendingDown className="h-5 w-5 text-amber-400" />
              Menor rotación
            </h1>
            <p className="mt-0.5 text-xs text-white/50">
              {loading
                ? 'Cargando productos...'
                : `${filtered.length} productos · +30 días en tienda sin ventas`}
            </p>
          </div>
        </div>

        {/* KPI chips */}
        {!loading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="border-amber-500/20 bg-amber-500/10 text-white backdrop-blur">
              <CardHeader className="pb-1 px-4 pt-3">
                <CardDescription className="text-xs text-amber-300/70">Productos estancados</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <p className="text-xl font-bold tabular-nums text-amber-400">{filtered.length}</p>
              </CardContent>
            </Card>
            <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
              <CardHeader className="pb-1 px-4 pt-3">
                <CardDescription className="text-xs text-white/50">Unidades inmovilizadas</CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <p className="text-xl font-bold tabular-nums text-white">
                  {totalInFiltered.toLocaleString('es-ES')}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
          <CardContent className="space-y-3 pt-4 pb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Buscar producto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-white/15 bg-white/10 py-2 pl-8 pr-8 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Vendor / Brand filter */}
            {brands.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-white/40">Proveedor / Marca</p>
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
                  <button
                    onClick={() => handleBrandChange('all')}
                    className={cn(
                      'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                      brandFilter === 'all'
                        ? 'border-amber-500 bg-amber-500 text-white'
                        : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                    )}
                  >
                    Todos
                  </button>
                  {brands.map((brand) => (
                    <button
                      key={brand}
                      onClick={() => handleBrandChange(brand === brandFilter ? 'all' : brand)}
                      className={cn(
                        'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                        brandFilter === brand
                          ? 'border-amber-500 bg-amber-500 text-white'
                          : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                      )}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product type / Line filter */}
            {productTypes.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-white/40">Línea de producto</p>
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
                  <button
                    onClick={() => setLineFilter('all')}
                    className={cn(
                      'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                      lineFilter === 'all'
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                    )}
                  >
                    Todas
                  </button>
                  {productTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => setLineFilter(type === lineFilter ? 'all' : type)}
                      className={cn(
                        'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                        lineFilter === type
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                      )}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sort + Estado + Stock */}
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <div className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-white/40">Ordenar</p>
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
                  {(Object.keys(sortLabels) as SortOption[]).map((key) => (
                    <button
                      key={key}
                      onClick={() => setSort(key)}
                      className={cn(
                        'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                        sort === key
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                      )}
                    >
                      {sortLabels[key]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-white/40">Estado</p>
                <div className="flex gap-1.5">
                  {([
                    { key: 'all', label: 'Todos', cls: 'border-blue-500 bg-blue-500' },
                    { key: 'active', label: 'Activos', cls: 'border-emerald-500 bg-emerald-500' },
                    { key: 'archived', label: 'Archivados', cls: 'border-red-500 bg-red-500' },
                  ] as const).map(({ key, label, cls }) => (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(key)}
                      className={cn(
                        'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                        statusFilter === key
                          ? `${cls} text-white`
                          : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-white/40">Stock</p>
                <div className="flex gap-1.5">
                  {([
                    { key: 'all', label: 'Todos' },
                    { key: 'low', label: '1–10' },
                    { key: 'medium', label: '11–50' },
                    { key: 'high', label: '+50' },
                  ] as const).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setInventoryRange(key)}
                      className={cn(
                        'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                        inventoryRange === key
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-white/15 bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-white/40 hover:text-white transition-colors"
              >
                <X className="h-3 w-3" />
                Limpiar filtros
              </button>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur overflow-hidden">
          <CardHeader className="px-5 py-4">
            <CardTitle className="text-sm font-semibold text-white">
              {loading ? (
                <Skeleton className="h-4 w-24 bg-white/10" />
              ) : (
                `${filtered.length} productos`
              )}
            </CardTitle>
            <CardDescription className="text-xs text-white/50">
              Ordenado por: {sortLabels[sort]}
              {brandFilter !== 'all' && ` · ${brandFilter}`}
              {lineFilter !== 'all' && ` · ${lineFilter}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-px">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex gap-3 px-4 py-3 border-b border-white/5">
                    <Skeleton className="h-14 w-14 flex-shrink-0 rounded-lg bg-white/10" />
                    <div className="flex-1 space-y-2 py-1">
                      <Skeleton className="h-4 w-3/4 bg-white/10" />
                      <Skeleton className="h-3 w-1/2 bg-white/10" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-12 text-center text-sm text-white/40">
                No hay productos que coincidan con los filtros.
              </p>
            ) : (
              <ul className="divide-y divide-white/5">
                {filtered.map((product, index) => {
                  const days = calculateDays(product.createdAt)
                  const inv = Number(product.inventory ?? 0)
                  const shopifyUrl = product.handle
                    ? `https://f3295a-3.myshopify.com/products/${product.handle}`
                    : null
                  const isCopied = copiedId === product.id

                  return (
                    <li key={product.id} className="px-4 py-3 hover:bg-white/5 transition-colors">
                      <div className="flex gap-3">
                        {/* Index + Image */}
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <span className="text-[10px] text-white/25 tabular-nums">{index + 1}</span>
                          <div className="h-14 w-14 overflow-hidden rounded-lg bg-amber-500/10 flex items-center justify-center">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.title}
                                className="h-full w-full object-cover rounded-lg"
                                loading="lazy"
                              />
                            ) : (
                              <TrendingDown className="h-5 w-5 text-amber-400/50" />
                            )}
                          </div>
                        </div>

                        {/* Info */}
                        <div className="min-w-0 flex-1">
                          {/* Title + copy button */}
                          <div className="flex items-start gap-1.5">
                            <p className="flex-1 text-sm font-medium text-white leading-snug line-clamp-2">
                              {product.title}
                            </p>
                            <button
                              onClick={() => handleCopy(product)}
                              title="Copiar nombre del producto"
                              className={cn(
                                'mt-0.5 flex-shrink-0 rounded p-1 transition-colors',
                                isCopied
                                  ? 'text-emerald-400'
                                  : 'text-white/30 hover:text-white hover:bg-white/10'
                              )}
                            >
                              {isCopied
                                ? <Check className="h-3.5 w-3.5" />
                                : <Copy className="h-3.5 w-3.5" />
                              }
                            </button>
                          </div>

                          {/* Meta: vendor + product_type */}
                          {(product.vendor || product.product_type) && (
                            <div className="mt-0.5 flex items-center gap-1.5">
                              {product.vendor && (
                                <span className="text-[10px] text-white/40">{product.vendor}</span>
                              )}
                              {product.vendor && product.product_type && (
                                <span className="text-[10px] text-white/20">·</span>
                              )}
                              {product.product_type && (
                                <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/50">
                                  {product.product_type}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Badges */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span
                              className={cn(
                                'text-xs font-semibold',
                                inv <= 5 ? 'text-red-400' : inv <= 15 ? 'text-amber-300' : 'text-emerald-400'
                              )}
                            >
                              {inv} en stock
                            </span>
                            {days !== undefined && (
                              <span className="text-xs text-white/40">{days} días en tienda</span>
                            )}
                            {product.status && (
                              <span
                                className={cn(
                                  'rounded px-1.5 py-0.5 text-[10px] font-medium',
                                  product.status === 'active'
                                    ? 'bg-emerald-500/20 text-emerald-400'
                                    : 'bg-red-500/20 text-red-400'
                                )}
                              >
                                {product.status === 'active' ? 'Activo' : 'Archivado'}
                              </span>
                            )}
                          </div>

                          {/* Shopify link */}
                          {shopifyUrl && (
                            <a
                              href={shopifyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/60 transition hover:bg-white/15 hover:text-white"
                            >
                              Ver en Shopify →
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
